import { formatArea } from "@evergis/react";
import { sGisMouseMoveEvent, DragEvent } from "@evergis/sgis/es/commonEvents";
import { PolyDragParams } from "@evergis/sgis/es/controls/PolyDrag";
import { Rectangle } from "@evergis/sgis/es/controls/Rectangle";
import { LabelFeature } from "@evergis/sgis/es/features/Label";
import { DomPainter } from "@evergis/sgis/es/painters/DomPainter/DomPainter";
import { Map as SMap } from "@evergis/sgis/es/Map";
import { DrawingBeginEvent, DrawingFinishEvent } from "@evergis/sgis/es/controls/Control";
import { area as calcArea } from "@evergis/sgis/es/geotools";
import { DEFAULT_LABEL_POSITION } from "../../utils";
import { textSymbol, tooltipSymbol } from "../TooltipSymbol";

export type BaseRectCreatorProps = PolyDragParams & {
  label?: string;
  showAreaLabel?: boolean;
  cursor?: "pointer" | "default" | "zoom-in" | "zoom-out";
};

export class BaseRectCreator extends Rectangle {
  private readonly painter: DomPainter;

  private tempLabel: LabelFeature;

  private _updateLabelPositionBind: (event: sGisMouseMoveEvent) => void;

  private readonly showAreaLabel: boolean;

  private readonly cursor: "pointer" | "default" | "zoom-in" | "zoom-out";

  constructor(map: SMap, painter: DomPainter, params: BaseRectCreatorProps) {
    const { label, showAreaLabel, cursor, ...rectangleParams } = params;

    super(map, rectangleParams);

    this.painter = painter;
    this.showAreaLabel = !!showAreaLabel;

    if (label || showAreaLabel) {
      this.tempLabel = new LabelFeature(DEFAULT_LABEL_POSITION, {
        crs: map.crs,
        symbol: showAreaLabel ? textSymbol : tooltipSymbol,
        content: label,
      });
    }

    if (cursor) {
      this.cursor = cursor;
    }
  }

  protected updateLabelPosition(event: sGisMouseMoveEvent) {
    this.tempLabel.position = event.point.position;
    this._tempLayer.redraw();
  }

  protected updateDrawing(event: DragEvent | sGisMouseMoveEvent) {
    super.updateDrawing(event);

    if (this.showAreaLabel && this._activeFeature) {
      const feature = this._activeFeature;

      const { rings, crs } = feature;

      const area = calcArea(rings, crs);

      this.tempLabel.content = formatArea(area);
      this.tempLabel.position = feature.centroid;
      this._tempLayer.redraw();
    }
  }

  protected _handleDrawStart() {
    this._tempLayer.remove(this.tempLabel);
    this._tempLayer.redraw();
  }

  protected _handleDrawFinish() {
    this._tempLayer.add(this.tempLabel);
    this._tempLayer.redraw();
  }

  protected _activate() {
    super._activate();
    if (this.cursor) {
      this.setCursor(this.cursor);
    } else {
      this.setCursor("pointer");
    }

    this.addLabel();
  }

  protected _deactivate() {
    super._deactivate();
    this.setCursor("default");

    this.removeLabel();
  }

  private addLabel() {
    if (this.tempLabel && !this._tempLayer.has(this.tempLabel)) {
      this._tempLayer.add(this.tempLabel);

      this._updateLabelPositionBind = this.updateLabelPosition.bind(this);

      if (!this.showAreaLabel) {
        this.map.on(sGisMouseMoveEvent.type, this._updateLabelPositionBind);
        this.on(DrawingBeginEvent.type, this._handleDrawStart);
        this.on(DrawingFinishEvent.type, this._handleDrawFinish);
      }
    }
  }

  private removeLabel() {
    if (this.tempLabel && this._tempLayer.has(this.tempLabel)) {
      this._tempLayer.remove(this.tempLabel);
      delete this.tempLabel;

      if (!this.showAreaLabel) {
        this.map.off(sGisMouseMoveEvent.type, this._updateLabelPositionBind);
        this.off(DrawingBeginEvent.type, this._handleDrawStart);
        this.off(DrawingFinishEvent.type, this._handleDrawFinish);
      }
    }
  }

  private setCursor(cursor: "pointer" | "default" | "zoom-in" | "zoom-out") {
    const { wrapper } = this.painter;

    if (wrapper && typeof wrapper !== "string") {
      wrapper.style.cursor = cursor;
    }
  }
}
